/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Copyright from '../components/Copyright';
import Logo from '../components/Logo';
import UserManagement from '../components/UserManagement';
import { Link, Outlet } from 'react-router-dom';
import SnackbarError from '../components/ErrorHandling/SnackbarError';
import Feedback from '../components/Feedback';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: toolbarHeight,
  backgroundColor: theme.palette.primary.appBar,
}));

const LogoButton = styled(Button)({
  '&:hover': {
    backgroundColor: 'rgba(100, 181, 246, 0.1)',
  },
});

const logoHeight = 55;
const toolbarHeight = logoHeight + 16;

const notificationCookie = 'notification_about_necessary_cookies=true';

function setNotificationCookie() {
  const oneYearInSeconds = 60 * 60 * 24 * 365;
  document.cookie = `${notificationCookie}; max_age=${oneYearInSeconds}`;
}

function isThereNotificationCookies() {
  return (document.cookie || '').split(';').includes(notificationCookie);
}

function LegalFooter() {
  const [showNotification, setState] = React.useState(
    !isThereNotificationCookies()
  );

  return (
    <Stack spacing={1} sx={{ mt: 'auto', mb: 4 }}>
      {showNotification && (
        <Alert
          variant="filled"
          severity="info"
          onClose={() => {
            setNotificationCookie();
            setState(false);
          }}
        >
          We use cookies to authenticate users on our website.
        </Alert>
      )}
      <Copyright />
    </Stack>
  );
}

export default function Dashboard() {
  return (
    <Box sx={{ display: 'flex' }}>
      <SnackbarError>
        <AppBar position="absolute">
          <Toolbar sx={{ pt: 1 }}>
            <LogoButton
              sx={{ p: 0 }}
              data-cy="logo-button"
              component={Link}
              to="/"
            >
              <Logo
                style={{
                  height: `${logoHeight}px`,
                }}
              />
            </LogoButton>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
              <Feedback />
              <UserManagement />
            </Box>
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.grey[100],
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            minHeight: '100vh',
            overflow: 'auto',
            pt: `${toolbarHeight}px`,
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              mt: 4,
              mb: 4,
              height: 1,
              width: 1,
            }}
          >
            <Outlet />
          </Container>
          <LegalFooter />
        </Box>
      </SnackbarError>
    </Box>
  );
}
